import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatAirlineAmount } from '../utils/currencyUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AIRLINE_ROLES = new Set(['airlines_admin', 'airlines_manager']);

const Topbar = ({ onToggleSidebar }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [airlineId, setAirlineId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (!user || !AIRLINE_ROLES.has(user.role)) {
      setAirlineId(null);
      setBalance(null);
      return undefined;
    }

    if (user.airlines_admin_id != null) {
      setAirlineId(user.airlines_admin_id);
    } else if (user.role === 'airlines_admin' && user.id != null) {
      setAirlineId(user.id);
    }

    let cancelled = false;

    const loadAirlineMeta = async () => {
      try {
        if (user.role === 'airlines_admin') {
          const res = await axios.get(`${API_URL}/airlines-admin/dashboard`);
          if (cancelled) return;
          setBalance(res.data?.balance ?? null);
          setCurrency(res.data?.profile?.currency || 'USD');
          setAirlineId(res.data?.profile?.id ?? user.id ?? null);
          return;
        }

        if (user.role === 'airlines_manager') {
          const res = await axios.get(`${API_URL}/airlines-manager/dashboard`);
          if (cancelled) return;
          setAirlineId(
            res.data?.airline?.id ?? user.airlines_admin_id ?? null
          );
        }
      } catch {
        if (!cancelled && user.role === 'airlines_admin') setBalance(null);
      }
    };

    loadAirlineMeta();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const profilePath = user?.role === 'airlines_admin' ? '/airlines-admin/profile' : null;
  const billingPath = user?.role === 'airlines_admin' ? '/airlines-admin/billing' : null;

  return (
    <header className="sticky top-0 z-40 bg-white shadow-header">
      <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3 min-h-[68px]">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-crm border border-border text-ink-muted hover:text-primary hover:border-primary"
          aria-label="Toggle sidebar"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
          </svg>
        </button>

        <div className="flex-1" />

        <div className="flex items-center gap-2 md:gap-3">
          {airlineId != null && (
            <div className="airline-id-chip hidden sm:inline-flex">
              Airline ID: {airlineId}
            </div>
          )}

          {user?.role === 'airlines_admin' && balance != null && (
            <>
              <div className="wallet-chip hidden sm:inline-flex">
                Balance: {formatAirlineAmount(balance, currency)}
              </div>
              {billingPath && (
                <button
                  type="button"
                  onClick={() => navigate(billingPath)}
                  className="wallet-chip"
                  title="Top up"
                >
                  +
                </button>
              )}
            </>
          )}

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="flex items-center gap-2 rounded-full focus:outline-none"
            >
              <div className="w-12 h-12 rounded-full border-2 border-emerald-500 overflow-hidden bg-primary-soft flex items-center justify-center text-base font-semibold text-primary">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-60 bg-white rounded-menu shadow-dropdown border border-border overflow-hidden">
                <div className="px-4 py-3 border-b border-border">
                  <p className="text-[13px] font-semibold text-ink truncate">{user?.name}</p>
                  <p className="text-xs text-ink-muted truncate capitalize">
                    {user?.role?.replace(/_/g, ' ')}
                  </p>
                  {airlineId != null && (
                    <p className="text-xs text-ink-muted mt-1">Airline ID: {airlineId}</p>
                  )}
                </div>
                {profilePath && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate(profilePath);
                    }}
                    className="w-full text-left px-4 py-2.5 text-[13px] font-medium text-ink hover:bg-[#fcfcfc] hover:text-primary"
                  >
                    Profile
                  </button>
                )}
                {billingPath && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate(billingPath);
                    }}
                    className="w-full text-left px-4 py-2.5 text-[13px] font-medium text-ink hover:bg-[#fcfcfc] hover:text-primary"
                  >
                    Billing & Payments
                  </button>
                )}
                <div className="p-3 border-t border-border">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full py-2 rounded-panel border border-[#dcdcdc] text-[13px] font-medium text-ink hover:border-primary hover:text-primary"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
